"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FilterX, Pencil, Plus, RotateCcw, Save, Trash2 } from "lucide-react";
import { useAppDialogs } from "@/components/dialogs/app-dialog-provider";
import { Button, EmptyState, ListCount, ListSurface, ListSurfaceBody, ListSurfaceHeader, ListToolbar, ListToolbarGroup, ToolbarIconButton } from "@/components/ds";
import { EnterprisePageShell } from "@/components/layout/enterprise-page-shell";
import { FlashToast } from "@/components/ui-system/flash-toast";
import { tableHeaderSearchInputClass, tableHeaderSelectTriggerClass, ThemedSelect } from "@/components/ui-system/select";
import { isAppHttpError } from "@/lib/api/client";
import { getDevices } from "@/lib/api/endpoints/devices";
import { getPermissionsMatrix, updateRolePermissions } from "@/lib/api/endpoints/permissions";
import {
  createRole,
  deleteRole,
  getRoleDeviceModulePermissions,
  listRoles,
  updateDefaultRoleWorkspaceDevicePermissions,
  updateRole,
  updateRoleDeviceModulePermissions,
} from "@/lib/api/endpoints/roles";
import type {
  PermissionAction,
  PermissionManagedRole,
  PermissionModuleDto,
  RoleModulePermissionDto,
} from "@/lib/api/types/enterprise";
import { isSuperAdmin } from "@/modules/auth/permissions";
import { hasModulePermission, useEffectivePermissions } from "@/modules/auth/access";
import { useAuthStore } from "@/modules/auth/store";
import { useWorkspaceChromeStore } from "@/store/workspace-chrome";

const ACTION_COLUMNS: Array<{ key: keyof RoleModulePermissionDto; label: string }> = [
  { key: "can_view", label: "View" },
  { key: "can_create", label: "Create" },
  { key: "can_edit", label: "Edit" },
  { key: "can_delete", label: "Delete" },
  { key: "can_manage_permissions", label: "Manage permissions" },
  { key: "can_export", label: "Export" },
];

const GROUP_LABELS: Record<string, string> = {
  core: "Core",
  platform: "Platform",
  reports: "Reports",
  system: "System",
};

const DEVICE_OPERATION_PERMISSION_MODULE_KEYS = new Set([
  "overview",
  "master_view",
  "devices",
  "live",
  "explorer",
  "analytics",
  "dashboards",
  "automations",
]);
const DEVICE_RELATED_PERMISSION_MODULE_KEYS = new Set([
  ...DEVICE_OPERATION_PERMISSION_MODULE_KEYS,
  "reports",
  "reports.channels",
  "reports.sales.time",
  "reports.sales.region",
  "reports.dataloggers.warranty",
  "reports.alerts",
]);
const HIDDEN_PERMISSION_MODULE_KEYS = new Set(["fleet", "organizations"]);
const SUPER_ADMIN_HIDDEN_PERMISSION_MODULE_KEYS = new Set([
  ...HIDDEN_PERMISSION_MODULE_KEYS,
  ...DEVICE_OPERATION_PERMISSION_MODULE_KEYS,
  "tenants",
  "security",
  "system_health",
  "entitlements",
]);
const ADMIN_ONLY_PERMISSION_MODULE_KEYS = new Set<string>();
const ADMIN_DEFAULT_GRANT_MODULE_KEYS = new Set([
  "overview",
  "master_view",
  "devices",
  "live",
  "explorer",
  "analytics",
  "dashboards",
  "automations",
]);
const DEFAULT_WORKSPACE_ROLE_KEYS = new Set(["distributor", "user"]);
const OTA_MODULE_KEY = "ota";

const PERMISSION_MODULE_OVERRIDES: Record<string, Partial<PermissionModuleDto>> = {
  automations: {
    label: "Alerts",
    description: "Create and maintain alert rules, audiences, and delivery actions.",
  },
  alerts: {
    label: "Notifications",
    description: "Read-only access to in-app notifications and alert history.",
    available_actions: ["view"],
  },
  locations: {
    label: "Locations",
    description: "View and manage locations for the user's assigned organization.",
  },
  reports: {
    label: "Report Delivery",
    description: "Create and schedule device-scoped report deliveries.",
  },
  data_management: {
    label: "Data Management",
    description: "View usage, clear storage, export and delete history, and remove OTA or image artifacts.",
  },
  roles: {
    label: "Roles",
    description: "Create custom roles and manage workspace permissions.",
  },
  master_view: {
    label: "Master View",
    description: "Latest channel snapshot grid for role-approved data loggers.",
    available_actions: ["view"],
  },
  support: {
    available_actions: ["view", "create", "edit", "delete"],
  },
};

type RolePermissionState = Record<PermissionManagedRole, RoleModulePermissionDto[]>;

function errorDetail(err: unknown): string {
  if (isAppHttpError(err)) return err.message;
  if (err instanceof Error) return err.message;
  return "Request failed";
}

function toState(data: Awaited<ReturnType<typeof getPermissionsMatrix>>): RolePermissionState {
  return data.assignments.reduce(
    (acc, assignment) => {
      acc[assignment.role] = assignment.permissions.map((permission) => ({ ...permission }));
      return acc;
    },
    {
      admin: [],
      distributor: [],
      user: [],
    } as RolePermissionState,
  );
}

function emptyPermission(moduleKey: string): RoleModulePermissionDto {
  return {
    module_key: moduleKey,
    can_view: false,
    can_create: false,
    can_edit: false,
    can_delete: false,
    can_manage_permissions: false,
    can_export: false,
  };
}

function clampPermissionToModule(
  permission: RoleModulePermissionDto,
  availableActions: string[],
): RoleModulePermissionDto {
  const actions = new Set(availableActions);
  return {
    ...permission,
    can_view: actions.has("view") && permission.can_view,
    can_create: permission.module_key !== "devices" && actions.has("create") && permission.can_create,
    can_edit: actions.has("edit") && permission.can_edit,
    can_delete: actions.has("delete") && permission.can_delete,
    can_export: actions.has("export") && permission.can_export,
    can_manage_permissions: actions.has("manage_permissions") && permission.can_manage_permissions,
  };
}

function isDisableOnlyOtaPermission(moduleKey: string, targetKind: "admin" | "distributor" | "user" | "custom"): boolean {
  if (moduleKey !== OTA_MODULE_KEY) {
    return false;
  }
  return targetKind === "user" || targetKind === "custom";
}

function canTogglePermissionCheckbox(options: {
  moduleKey: string;
  targetKind: "admin" | "distributor" | "user" | "custom";
  checked: boolean;
  baseCanGrant: boolean;
}): boolean {
  if (isDisableOnlyOtaPermission(options.moduleKey, options.targetKind)) {
    return options.checked || options.baseCanGrant;
  }
  return options.baseCanGrant;
}

type PermissionChange = {
  moduleKey: string;
  action: keyof RoleModulePermissionDto;
  checked: boolean;
};

function applyPermissionChanges(current: RoleModulePermissionDto[], changes: PermissionChange[]): RoleModulePermissionDto[] {
  if (changes.length === 0) return current;
  const changesByModule = changes.reduce<Map<string, PermissionChange[]>>((acc, change) => {
    acc.set(change.moduleKey, [...(acc.get(change.moduleKey) ?? []), change]);
    return acc;
  }, new Map());

  const existingModuleKeys = new Set(current.map((permission) => permission.module_key));
  const updated = current.map((permission) => {
    const moduleChanges = changesByModule.get(permission.module_key);
    if (!moduleChanges) return permission;
    return moduleChanges.reduce<RoleModulePermissionDto>(
      (next, change) => ({ ...next, [change.action]: change.checked }),
      permission,
    );
  });

  changesByModule.forEach((moduleChanges, moduleKey) => {
    if (!existingModuleKeys.has(moduleKey)) {
      updated.push(
        moduleChanges.reduce<RoleModulePermissionDto>(
          (next, change) => ({ ...next, [change.action]: change.checked }),
          emptyPermission(moduleKey),
        ),
      );
    }
  });
  return updated;
}

function BulkPermissionCheckbox({
  checked,
  values,
  disabled = false,
  label,
  onChange,
}: {
  checked: boolean;
  values: boolean[];
  disabled?: boolean;
  label: string;
  onChange: (checked: boolean) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const hasEligiblePermissions = values.length > 0;
  const isIndeterminate = values.some(Boolean) && !values.every(Boolean);

  useEffect(() => {
    if (inputRef.current) inputRef.current.indeterminate = isIndeterminate;
  }, [isIndeterminate]);

  return (
    <input
      ref={inputRef}
      type="checkbox"
      checked={hasEligiblePermissions && checked}
      disabled={disabled || !hasEligiblePermissions}
      aria-label={label}
      onChange={(event) => onChange(event.target.checked)}
      className="h-4 w-4 cursor-pointer rounded border-[#d1d5db] text-[var(--btn-primary-to)] focus:ring-[var(--btn-primary-to)]/20 disabled:cursor-not-allowed disabled:opacity-30"
    />
  );
}

export function PermissionsPage() {
  const dialogs = useAppDialogs();
  const user = useAuthStore((s) => s.user);
  const tenantId = useAuthStore((s) => s.tenantId);
  const setWorkspaceChrome = useWorkspaceChromeStore((s) => s.setWorkspaceChrome);
  const resetWorkspaceChrome = useWorkspaceChromeStore((s) => s.resetWorkspaceChrome);
  const qc = useQueryClient();
  const effectivePermissions = useEffectivePermissions();
  const [selectedRole, setSelectedRole] = useState<PermissionManagedRole>("admin");
  const [search, setSearch] = useState("");
  const [toast, setToast] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [customRoleName, setCustomRoleName] = useState("");
  const [customRoleDescription, setCustomRoleDescription] = useState("");
  const [selectedCustomRoleId, setSelectedCustomRoleId] = useState<string | null>(null);
  const [roleDialog, setRoleDialog] = useState<"create" | "edit" | null>(null);
  const [customRoleEditName, setCustomRoleEditName] = useState("");
  const [customRoleEditDescription, setCustomRoleEditDescription] = useState("");
  const [customRolePermissions, setCustomRolePermissions] = useState<RoleModulePermissionDto[]>([]);
  const [customRoleDevicePermissions, setCustomRoleDevicePermissions] = useState<RoleModulePermissionDto[]>([]);
  const [selectedPermissionDeviceId, setSelectedPermissionDeviceId] = useState("");
  const [drafts, setDrafts] = useState<RolePermissionState>({
    admin: [],
    distributor: [],
    user: [],
  });
  const grantableCustomRoleModules = useMemo(
    () => (effectivePermissions.data?.modules ?? []).filter((module) => !HIDDEN_PERMISSION_MODULE_KEYS.has(module.module_key)),
    [effectivePermissions.data?.modules],
  );
  const grantableWorkspaceRoleModules = useMemo(
    () => grantableCustomRoleModules.filter((module) => !DEVICE_RELATED_PERMISSION_MODULE_KEYS.has(module.module_key)),
    [grantableCustomRoleModules],
  );
  const grantableDeviceRoleModules = useMemo(
    () => grantableCustomRoleModules.filter((module) => DEVICE_RELATED_PERMISSION_MODULE_KEYS.has(module.module_key)),
    [grantableCustomRoleModules],
  );
  const canCreateCustomRoles = hasModulePermission(user, effectivePermissions.permissionMap, "roles", "create");
  const canEditCustomRoles = hasModulePermission(user, effectivePermissions.permissionMap, "roles", "edit");
  const canDeleteCustomRoles = hasModulePermission(user, effectivePermissions.permissionMap, "roles", "delete");
  const canManageCustomRolePermissions = hasModulePermission(
    user,
    effectivePermissions.permissionMap,
    "roles",
    "manage_permissions",
  );

  const query = useQuery({
    queryKey: ["permissions-matrix"],
    queryFn: getPermissionsMatrix,
    enabled: isSuperAdmin(user),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
  const customRolesQuery = useQuery({
    queryKey: ["roles", tenantId ?? "none", "workspace-role-management"],
    queryFn: () => listRoles(tenantId, true),
    enabled: !isSuperAdmin(user) && Boolean(tenantId),
  });
  const permissionDevicesQuery = useQuery({
    queryKey: ["devices", tenantId ?? "none", "permissions-device-grants"],
    queryFn: () => getDevices(tenantId ?? null),
    enabled: !isSuperAdmin(user) && Boolean(tenantId) && roleDialog === "edit",
    staleTime: 30_000,
  });
  const roleDevicePermissionsQuery = useQuery({
    queryKey: ["roles", selectedCustomRoleId ?? "none", "device-module-permissions", selectedPermissionDeviceId || "none"],
    queryFn: () => getRoleDeviceModulePermissions(selectedCustomRoleId!, selectedPermissionDeviceId),
    enabled: Boolean(selectedCustomRoleId && selectedPermissionDeviceId && roleDialog === "edit" && canManageCustomRolePermissions),
    staleTime: 15_000,
  });

  useEffect(() => {
    setWorkspaceChrome({
      title: isSuperAdmin(user) ? "Permissions" : "Roles",
      search: null,
    });
    return () => resetWorkspaceChrome();
  }, [resetWorkspaceChrome, setWorkspaceChrome, user]);

  useEffect(() => {
    if (!isSuperAdmin(user) && user?.id) {
      void effectivePermissions.refetch();
    }
  }, [effectivePermissions.refetch, user]);

  useEffect(() => {
    if (query.data) {
      setDrafts(toState(query.data));
    }
  }, [query.data]);

  const selectedCustomRole = (customRolesQuery.data?.items ?? []).find(
    (role) => role.id === selectedCustomRoleId,
  );

  useEffect(() => {
    if (!selectedCustomRole) return;
    setCustomRoleEditName(selectedCustomRole.name);
    setCustomRoleEditDescription(selectedCustomRole.description ?? "");
    if (selectedCustomRole.is_default) {
      setCustomRolePermissions(
        grantableWorkspaceRoleModules.map((module) => {
          const permission =
            selectedCustomRole.permissions.find((item) => item.module_key === module.module_key) ??
            emptyPermission(module.module_key);
          return clampPermissionToModule(permission, module.available_actions);
        }),
      );
      return;
    }
    setCustomRolePermissions(
      selectedCustomRole.permissions
        .filter((permission) => !DEVICE_RELATED_PERMISSION_MODULE_KEYS.has(permission.module_key))
        .map((permission) => ({ ...permission })),
    );
  }, [grantableWorkspaceRoleModules, selectedCustomRole]);

  useEffect(() => {
    if (permissionDevicesQuery.isLoading) return;
    const devices = permissionDevicesQuery.data ?? [];
    if (devices.length === 0) {
      setSelectedPermissionDeviceId("");
      return;
    }
    if (!selectedPermissionDeviceId || !devices.some((device) => device.id === selectedPermissionDeviceId)) {
      setSelectedPermissionDeviceId(devices[0]?.id ?? "");
    }
  }, [permissionDevicesQuery.data, permissionDevicesQuery.isLoading, selectedPermissionDeviceId]);

  useEffect(() => {
    if (!roleDevicePermissionsQuery.data) {
      if (!selectedPermissionDeviceId) {
        setCustomRoleDevicePermissions([]);
      }
      return;
    }
    setCustomRoleDevicePermissions(
      grantableDeviceRoleModules.map((module) => {
        const permission =
          roleDevicePermissionsQuery.data.permissions.find((item) => item.module_key === module.module_key) ??
          emptyPermission(module.module_key);
        return clampPermissionToModule(permission, module.available_actions);
      }),
    );
  }, [grantableDeviceRoleModules, roleDevicePermissionsQuery.data, selectedPermissionDeviceId]);

  const roles = query.data?.roles ?? [];
  const modules = useMemo(
    () =>
      (query.data?.modules ?? [])
        .filter((module) => !SUPER_ADMIN_HIDDEN_PERMISSION_MODULE_KEYS.has(module.key))
        .filter((module) => selectedRole === "admin" || !ADMIN_ONLY_PERMISSION_MODULE_KEYS.has(module.key))
        .map((module) => ({
          ...module,
          ...PERMISSION_MODULE_OVERRIDES[module.key],
        })),
    [query.data?.modules, selectedRole],
  );

  const selectedRoleLabel =
    roles.find((role) => role.key === selectedRole)?.label ?? selectedRole;

  const filteredModules = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) {
      return modules;
    }
    return modules.filter((module) => {
      const haystack = [module.label, module.key, module.description ?? "", module.nav_group]
        .join(" ")
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [modules, search]);

  const modulesByKey = useMemo(
    () =>
      modules.reduce<Record<string, PermissionModuleDto>>((acc, module) => {
        acc[module.key] = module;
        return acc;
      }, {}),
    [modules],
  );

  const groupedModules = useMemo(() => {
    return filteredModules.reduce<Record<string, PermissionModuleDto[]>>((acc, module) => {
      const group = module.nav_group;
      acc[group] = acc[group] ?? [];
      acc[group].push(module);
      return acc;
    }, {});
  }, [filteredModules]);

  const activePermissions = drafts[selectedRole] ?? [];
  const customRoles = (customRolesQuery.data?.items ?? []).filter(
    (role) => (!role.is_system && !role.is_default) || (role.is_default && DEFAULT_WORKSPACE_ROLE_KEYS.has(role.key)),
  );
  const filteredCustomRoles = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return customRoles;
    return customRoles.filter((role) =>
      [role.name, role.key, role.description ?? ""].join(" ").toLowerCase().includes(term),
    );
  }, [customRoles, search]);
  const customRoleListError = customRolesQuery.isError ? errorDetail(customRolesQuery.error) : null;
  const customRoleListMessage = !tenantId
    ? "Select a workspace before managing custom roles."
    : customRoleListError;
  const permissionMap = useMemo(
    () =>
      activePermissions.reduce<Record<string, RoleModulePermissionDto>>((acc, permission) => {
        acc[permission.module_key] = permission;
        return acc;
      }, {}),
    [activePermissions],
  );

  const saveMutation = useMutation({
    mutationFn: async () =>
      updateRolePermissions(selectedRole, {
        permissions: (drafts[selectedRole] ?? []).filter(
          (permission) => !SUPER_ADMIN_HIDDEN_PERMISSION_MODULE_KEYS.has(permission.module_key),
        ),
      }),
    onSuccess: async (result) => {
      setDrafts((prev) => ({ ...prev, [selectedRole]: result.permissions.map((item) => ({ ...item })) }));
      await qc.invalidateQueries({ queryKey: ["permissions-matrix"] });
      setFormError(null);
      setToast(`${selectedRoleLabel} permissions saved.`);
    },
    onError: (error) => setFormError(errorDetail(error)),
  });
  const createCustomRoleMutation = useMutation({
    mutationFn: () =>
      createRole({
        name: customRoleName.trim(),
        description: customRoleDescription.trim() || null,
        permissions: [],
      }),
    onSuccess: async () => {
      setCustomRoleName("");
      setCustomRoleDescription("");
      setRoleDialog(null);
      setFormError(null);
      setToast("Custom role created.");
      await qc.invalidateQueries({ queryKey: ["roles"] });
    },
    onError: (error) => setFormError(errorDetail(error)),
  });
  const saveCustomRoleMutation = useMutation({
    mutationFn: async () => {
      if (!selectedCustomRoleId) throw new Error("Select a custom role.");
      const workspacePermissions = customRolePermissions.filter(
        (permission) => !DEVICE_RELATED_PERMISSION_MODULE_KEYS.has(permission.module_key),
      );
      const devicePermissions = customRoleDevicePermissions.filter(
        (permission) => DEVICE_RELATED_PERMISSION_MODULE_KEYS.has(permission.module_key),
      );
      if (selectedCustomRole?.is_default) {
        const grantableModuleKeys = new Set(grantableWorkspaceRoleModules.map((module) => module.module_key));
        await updateDefaultRoleWorkspaceDevicePermissions(
          selectedCustomRoleId,
          workspacePermissions.filter((permission) => grantableModuleKeys.has(permission.module_key)),
        );
      } else {
        await updateRole(selectedCustomRoleId, {
          name: customRoleEditName.trim(),
          description: customRoleEditDescription.trim() || null,
          permissions: canManageCustomRolePermissions ? workspacePermissions : undefined,
        });
      }
      if (canManageCustomRolePermissions && selectedPermissionDeviceId) {
        await updateRoleDeviceModulePermissions(
          selectedCustomRoleId,
          selectedPermissionDeviceId,
          devicePermissions,
        );
      }
    },
    onSuccess: async () => {
      setRoleDialog(null);
      setSelectedCustomRoleId(null);
      setFormError(null);
      setToast("Custom role saved.");
      await qc.invalidateQueries({ queryKey: ["roles"] });
      await qc.invalidateQueries({ queryKey: ["devices"] });
    },
    onError: (error) => setFormError(errorDetail(error)),
  });
  const deleteCustomRoleMutation = useMutation({
    mutationFn: (roleId: string) => deleteRole(roleId),
    onSuccess: async () => {
      setSelectedCustomRoleId(null);
      setFormError(null);
      setToast("Custom role deleted.");
      await qc.invalidateQueries({ queryKey: ["roles"] });
    },
    onError: (error) => setFormError(errorDetail(error)),
  });

  const resetFromServer = () => {
    if (!query.data) return;
    setDrafts(toState(query.data));
    setFormError(null);
  };

  if (!isSuperAdmin(user)) {
    return (
      <EnterprisePageShell
        hideHeader
        title="Roles"
        description="Create and manage workspace-scoped custom roles."
      >
        {toast ? <FlashToast message={toast} onDone={() => setToast(null)} /> : null}
        <div className="grid gap-5">
          {formError ? (
            <div className="rounded-control border border-error/30 bg-error/10 px-4 py-3 text-caption text-error" role="alert">{formError}</div>
          ) : null}
          {roleDialog ? (
            <div
              className="fixed inset-0 z-[1200] flex items-center justify-center bg-slate-950/25 p-6 backdrop-blur-sm"
              role="dialog"
              aria-modal="true"
              onClick={() => {
                setRoleDialog(null);
                setSelectedCustomRoleId(null);
              }}
            >
              <div
                className="flex max-h-[calc(100dvh-48px)] w-full max-w-4xl flex-col overflow-hidden rounded-card border border-card-border bg-surface-elevated shadow-overlay"
                onClick={(event) => event.stopPropagation()}
              >
                <div className="flex items-start justify-between gap-4 border-b border-card-border bg-surface-secondary px-5 py-4">
                  <div>
                    <h2 className="text-sm font-semibold text-foreground">
                      {roleDialog === "create" ? "Create role" : "Edit role"}
                    </h2>
                    <p className="mt-1 text-sm text-muted">
                      {roleDialog === "create"
                        ? "Create a custom role for the active workspace."
                        : "Update role details and grant permissions within your access."}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setRoleDialog(null);
                      setSelectedCustomRoleId(null);
                    }}
                    className="h-9 rounded-lg border border-card-border px-3 text-sm font-semibold text-foreground"
                  >
                    Close
                  </button>
                </div>
                {roleDialog === "create" ? (
                  <div className="grid gap-3 overflow-y-auto p-5">
                    <label className="grid gap-1.5">
                      <span className="text-xs font-semibold text-foreground">Name</span>
                      <input
                        value={customRoleName}
                        onChange={(event) => setCustomRoleName(event.target.value)}
                        placeholder="Role name"
                        className="ui-control"
                      />
                    </label>
                    <label className="grid gap-1.5">
                      <span className="text-xs font-semibold text-foreground">Description</span>
                      <input
                        value={customRoleDescription}
                        onChange={(event) => setCustomRoleDescription(event.target.value)}
                        placeholder="Description"
                        className="ui-control"
                      />
                    </label>
                  </div>
                ) : selectedCustomRole ? (
                  <div className="grid gap-4 overflow-y-auto p-5">
                    {selectedCustomRole.is_default ? (
                      <div className="rounded-control border border-card-border bg-surface-secondary px-4 py-3">
                        <div className="text-sm font-semibold text-foreground">{selectedCustomRole.name}</div>
                        <p className="mt-1 text-xs text-muted">
                          Default role: manage workspace permissions within your granted access.
                        </p>
                      </div>
                    ) : (
                      <>
                        <label className="grid gap-1.5">
                          <span className="text-xs font-semibold text-foreground">Name</span>
                          <input
                            value={customRoleEditName}
                            onChange={(event) => setCustomRoleEditName(event.target.value)}
                            placeholder="Role name"
                            className="ui-control h-10 rounded-control bg-surface-primary px-3 text-caption"
                          />
                        </label>
                        <label className="grid gap-1.5">
                          <span className="text-xs font-semibold text-foreground">Description</span>
                          <input
                            value={customRoleEditDescription}
                            onChange={(event) => setCustomRoleEditDescription(event.target.value)}
                            placeholder="Description"
                            className="ui-control h-10 rounded-control bg-surface-primary px-3 text-caption"
                          />
                        </label>
                      </>
                    )}
                    {canManageCustomRolePermissions ? (
                      <div className="grid gap-4">
                        <div className="overflow-x-auto rounded-xl border border-card-border">
                          <div className="flex items-center justify-between gap-3 border-b border-card-border bg-surface-secondary px-3 py-2">
                            <div>
                              <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                                <BulkPermissionCheckbox
                                  checked={grantableWorkspaceRoleModules.every((module) =>
                                    ACTION_COLUMNS.filter((column) => {
                                      const action = column.key.replace("can_", "") as PermissionAction;
                                      return module.available_actions.includes(action) && Boolean((module as unknown as Record<string, unknown>)[column.key]);
                                    }).every((column) => {
                                      const draft = customRolePermissions.find((permission) => permission.module_key === module.module_key) ?? emptyPermission(module.module_key);
                                      return Boolean(draft[column.key]);
                                    }),
                                  )}
                                  values={grantableWorkspaceRoleModules.flatMap((module) => {
                                    const draft = customRolePermissions.find((permission) => permission.module_key === module.module_key) ?? emptyPermission(module.module_key);
                                    return ACTION_COLUMNS.filter((column) => {
                                      const action = column.key.replace("can_", "") as PermissionAction;
                                      return module.available_actions.includes(action) && Boolean((module as unknown as Record<string, unknown>)[column.key]);
                                    }).map((column) => Boolean(draft[column.key]));
                                  })}
                                  disabled={grantableWorkspaceRoleModules.length === 0}
                                  label="Select all workspace module permissions"
                                  onChange={(checked) => setCustomRolePermissions((current) =>
                                    applyPermissionChanges(current, grantableWorkspaceRoleModules.flatMap((module) =>
                                      ACTION_COLUMNS.flatMap((column) => {
                                        const action = column.key.replace("can_", "") as PermissionAction;
                                        const draft = current.find((permission) => permission.module_key === module.module_key) ?? emptyPermission(module.module_key);
                                        const canToggle = canTogglePermissionCheckbox({
                                          moduleKey: module.module_key,
                                          targetKind: selectedCustomRole.is_default ? ((selectedCustomRole.key as "distributor" | "user") ?? "user") : "custom",
                                          checked: Boolean(draft[column.key]),
                                          baseCanGrant: module.available_actions.includes(action) && Boolean((module as unknown as Record<string, unknown>)[column.key]),
                                        });
                                        return canToggle ? [{ moduleKey: module.module_key, action: column.key, checked }] : [];
                                      }),
                                    )),
                                  )}
                                />
                                Workspace modules
                              </div>
                              <p className="text-xs text-muted">Permissions that are not tied to one data logger.</p>
                            </div>
                          </div>
                          <table className="min-w-full">
                            <thead>
                              <tr>
                                <th className="px-3 py-2 text-left text-xs font-semibold text-muted">Module</th>
                                {ACTION_COLUMNS.map((column) => (
                                  <th key={column.key} className="px-3 py-2 text-center text-xs font-semibold text-muted">
                                    <label className="inline-flex cursor-pointer items-center justify-center gap-1.5" title={`Toggle ${column.label} for workspace modules`}>
                                      <BulkPermissionCheckbox
                                        checked={grantableWorkspaceRoleModules.filter((module) => module.available_actions.includes(column.key.replace("can_", "") as PermissionAction) && Boolean((module as unknown as Record<string, unknown>)[column.key])).every((module) => Boolean((customRolePermissions.find((permission) => permission.module_key === module.module_key) ?? emptyPermission(module.module_key))[column.key]))}
                                        values={grantableWorkspaceRoleModules.filter((module) => module.available_actions.includes(column.key.replace("can_", "") as PermissionAction) && Boolean((module as unknown as Record<string, unknown>)[column.key])).map((module) => Boolean((customRolePermissions.find((permission) => permission.module_key === module.module_key) ?? emptyPermission(module.module_key))[column.key]))}
                                        disabled={!grantableWorkspaceRoleModules.some((module) => module.available_actions.includes(column.key.replace("can_", "") as PermissionAction) && Boolean((module as unknown as Record<string, unknown>)[column.key]))}
                                        label={`Toggle ${column.label} for workspace modules`}
                                        onChange={(checked) => setCustomRolePermissions((current) => applyPermissionChanges(current, grantableWorkspaceRoleModules.flatMap((module) => {
                                          const action = column.key.replace("can_", "") as PermissionAction;
                                          const draft = current.find((permission) => permission.module_key === module.module_key) ?? emptyPermission(module.module_key);
                                          const canToggle = canTogglePermissionCheckbox({ moduleKey: module.module_key, targetKind: selectedCustomRole.is_default ? ((selectedCustomRole.key as "distributor" | "user") ?? "user") : "custom", checked: Boolean(draft[column.key]), baseCanGrant: module.available_actions.includes(action) && Boolean((module as unknown as Record<string, unknown>)[column.key]) });
                                          return canToggle ? [{ moduleKey: module.module_key, action: column.key, checked }] : [];
                                        })))}
                                      />
                                      <span>{column.label}</span>
                                    </label>
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {grantableWorkspaceRoleModules.map((module) => {
                                const draft =
                                  customRolePermissions.find((permission) => permission.module_key === module.module_key) ??
                                  emptyPermission(module.module_key);
                                return (
                                  <tr key={module.module_key} className="border-t border-card-border">
                                    <td className="px-3 py-2 text-sm font-medium text-foreground">
                                      <label className="inline-flex cursor-pointer items-center gap-2" title={`Toggle all permissions for ${module.label}`}>
                                        <BulkPermissionCheckbox
                                          checked={ACTION_COLUMNS.filter((column) => {
                                            const action = column.key.replace("can_", "") as PermissionAction;
                                            return module.available_actions.includes(action) && Boolean((module as unknown as Record<string, unknown>)[column.key]);
                                          }).every((column) => Boolean(draft[column.key]))}
                                          values={ACTION_COLUMNS.filter((column) => {
                                            const action = column.key.replace("can_", "") as PermissionAction;
                                            return module.available_actions.includes(action) && Boolean((module as unknown as Record<string, unknown>)[column.key]);
                                          }).map((column) => Boolean(draft[column.key]))}
                                          label={`Toggle all permissions for ${module.label}`}
                                          onChange={(checked) => setCustomRolePermissions((current) => applyPermissionChanges(current, ACTION_COLUMNS.flatMap((column) => {
                                            const action = column.key.replace("can_", "") as PermissionAction;
                                            const currentDraft = current.find((permission) => permission.module_key === module.module_key) ?? emptyPermission(module.module_key);
                                            const canToggle = canTogglePermissionCheckbox({ moduleKey: module.module_key, targetKind: selectedCustomRole.is_default ? ((selectedCustomRole.key as "distributor" | "user") ?? "user") : "custom", checked: Boolean(currentDraft[column.key]), baseCanGrant: module.available_actions.includes(action) && Boolean((module as unknown as Record<string, unknown>)[column.key]) });
                                            return canToggle ? [{ moduleKey: module.module_key, action: column.key, checked }] : [];
                                          })))}
                                        />
                                        <span>{module.label}</span>
                                      </label>
                                    </td>
                                    {ACTION_COLUMNS.map((column) => {
                                      const action = column.key.replace("can_", "") as PermissionAction;
                                      const canGrant =
                                        module.available_actions.includes(action) &&
                                        Boolean((module as unknown as Record<string, unknown>)[column.key]);
                                      const canToggle = canTogglePermissionCheckbox({
                                        moduleKey: module.module_key,
                                        targetKind: selectedCustomRole.is_default
                                          ? ((selectedCustomRole.key as "distributor" | "user") ?? "user")
                                          : "custom",
                                        checked: Boolean(draft[column.key]),
                                        baseCanGrant: canGrant,
                                      });
                                      return (
                                        <td key={column.key} className="px-3 py-2 text-center">
                                          <input
                                            type="checkbox"
                                            checked={Boolean(draft[column.key])}
                                            disabled={!canToggle}
                                            onChange={(event) => {
                                              const checked = event.target.checked;
                                              setCustomRolePermissions((current) => {
                                                const found = current.some((item) => item.module_key === module.module_key);
                                                if (!found) {
                                                  return [...current, { ...emptyPermission(module.module_key), [column.key]: checked }];
                                                }
                                                return current.map((item) =>
                                                  item.module_key === module.module_key
                                                    ? { ...item, [column.key]: checked }
                                                    : item,
                                                );
                                              });
                                            }}
                                            className="h-4 w-4 disabled:opacity-30"
                                          />
                                        </td>
                                      );
                                    })}
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>

                        <div className="overflow-x-auto rounded-xl border border-card-border">
                          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-card-border bg-surface-secondary px-3 py-2">
                            <div>
                              <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                                <BulkPermissionCheckbox
                                  checked={grantableDeviceRoleModules.flatMap((module) => ACTION_COLUMNS.filter((column) => {
                                    const action = column.key.replace("can_", "") as PermissionAction;
                                    const adminDefaultGrant = user?.role === "admin" && ADMIN_DEFAULT_GRANT_MODULE_KEYS.has(module.module_key) && module.available_actions.includes(action);
                                    return selectedPermissionDeviceId.length > 0 && (adminDefaultGrant || (module.available_actions.includes(action) && Boolean((module as unknown as Record<string, unknown>)[column.key])));
                                  }).map((column) => Boolean((customRoleDevicePermissions.find((permission) => permission.module_key === module.module_key) ?? emptyPermission(module.module_key))[column.key]))).every(Boolean)}
                                  values={grantableDeviceRoleModules.flatMap((module) => ACTION_COLUMNS.filter((column) => {
                                    const action = column.key.replace("can_", "") as PermissionAction;
                                    const adminDefaultGrant = user?.role === "admin" && ADMIN_DEFAULT_GRANT_MODULE_KEYS.has(module.module_key) && module.available_actions.includes(action);
                                    return selectedPermissionDeviceId.length > 0 && (adminDefaultGrant || (module.available_actions.includes(action) && Boolean((module as unknown as Record<string, unknown>)[column.key])));
                                  }).map((column) => Boolean((customRoleDevicePermissions.find((permission) => permission.module_key === module.module_key) ?? emptyPermission(module.module_key))[column.key])))}
                                  disabled={!selectedPermissionDeviceId || roleDevicePermissionsQuery.isLoading}
                                  label="Select all device module permissions"
                                  onChange={(checked) => setCustomRoleDevicePermissions((current) => applyPermissionChanges(current, grantableDeviceRoleModules.flatMap((module) => ACTION_COLUMNS.flatMap((column) => {
                                    const action = column.key.replace("can_", "") as PermissionAction;
                                    const adminDefaultGrant = user?.role === "admin" && ADMIN_DEFAULT_GRANT_MODULE_KEYS.has(module.module_key) && module.available_actions.includes(action);
                                    const draft = current.find((permission) => permission.module_key === module.module_key) ?? emptyPermission(module.module_key);
                                    const canToggle = canTogglePermissionCheckbox({ moduleKey: module.module_key, targetKind: selectedCustomRole?.is_default ? ((selectedCustomRole.key as "distributor" | "user") ?? "user") : "custom", checked: Boolean(draft[column.key]), baseCanGrant: selectedPermissionDeviceId.length > 0 && (adminDefaultGrant || (module.available_actions.includes(action) && Boolean((module as unknown as Record<string, unknown>)[column.key]))) });
                                    return canToggle ? [{ moduleKey: module.module_key, action: column.key, checked }] : [];
                                  }))))}
                                />
                                Device modules
                              </div>
                              <p className="text-xs text-muted">Select one data logger, then grant module access for that device.</p>
                            </div>
                            <ThemedSelect
                              value={selectedPermissionDeviceId}
                              onChange={(value) => {
                                setSelectedPermissionDeviceId(value);
                                setCustomRoleDevicePermissions([]);
                              }}
                              options={[
                                {
                                  value: "",
                                  label: permissionDevicesQuery.isLoading ? "Loading devices..." : "Select data logger",
                                },
                                ...(permissionDevicesQuery.data ?? []).map((device) => ({
                                  value: device.id,
                                  label: device.name?.trim() || device.device_uid,
                                  description: device.device_uid,
                                })),
                              ]}
                              ariaLabel="Device for role permissions"
                              searchable
                              className="min-w-[240px]"
                            />
                          </div>
                          <table className="min-w-full">
                            <thead>
                              <tr>
                                <th className="px-3 py-2 text-left text-xs font-semibold text-muted">Module</th>
                                {ACTION_COLUMNS.map((column) => (
                                  <th key={column.key} className="px-3 py-2 text-center text-xs font-semibold text-muted">
                                    <label className="inline-flex cursor-pointer items-center justify-center gap-1.5" title={`Toggle ${column.label} for device modules`}>
                                      <BulkPermissionCheckbox
                                        checked={grantableDeviceRoleModules.filter((module) => {
                                          const action = column.key.replace("can_", "") as PermissionAction;
                                          const adminDefaultGrant = user?.role === "admin" && ADMIN_DEFAULT_GRANT_MODULE_KEYS.has(module.module_key) && module.available_actions.includes(action);
                                          return selectedPermissionDeviceId.length > 0 && (adminDefaultGrant || (module.available_actions.includes(action) && Boolean((module as unknown as Record<string, unknown>)[column.key])));
                                        }).every((module) => Boolean((customRoleDevicePermissions.find((permission) => permission.module_key === module.module_key) ?? emptyPermission(module.module_key))[column.key]))}
                                        values={grantableDeviceRoleModules.filter((module) => {
                                          const action = column.key.replace("can_", "") as PermissionAction;
                                          const adminDefaultGrant = user?.role === "admin" && ADMIN_DEFAULT_GRANT_MODULE_KEYS.has(module.module_key) && module.available_actions.includes(action);
                                          return selectedPermissionDeviceId.length > 0 && (adminDefaultGrant || (module.available_actions.includes(action) && Boolean((module as unknown as Record<string, unknown>)[column.key])));
                                        }).map((module) => Boolean((customRoleDevicePermissions.find((permission) => permission.module_key === module.module_key) ?? emptyPermission(module.module_key))[column.key]))}
                                        disabled={!selectedPermissionDeviceId || roleDevicePermissionsQuery.isLoading}
                                        label={`Toggle ${column.label} for device modules`}
                                        onChange={(checked) => setCustomRoleDevicePermissions((current) => applyPermissionChanges(current, grantableDeviceRoleModules.flatMap((module) => {
                                          const action = column.key.replace("can_", "") as PermissionAction;
                                          const adminDefaultGrant = user?.role === "admin" && ADMIN_DEFAULT_GRANT_MODULE_KEYS.has(module.module_key) && module.available_actions.includes(action);
                                          const draft = current.find((permission) => permission.module_key === module.module_key) ?? emptyPermission(module.module_key);
                                          const canToggle = canTogglePermissionCheckbox({ moduleKey: module.module_key, targetKind: selectedCustomRole?.is_default ? ((selectedCustomRole.key as "distributor" | "user") ?? "user") : "custom", checked: Boolean(draft[column.key]), baseCanGrant: selectedPermissionDeviceId.length > 0 && (adminDefaultGrant || (module.available_actions.includes(action) && Boolean((module as unknown as Record<string, unknown>)[column.key]))) });
                                          return canToggle ? [{ moduleKey: module.module_key, action: column.key, checked }] : [];
                                        })))}
                                      />
                                      <span>{column.label}</span>
                                    </label>
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {grantableDeviceRoleModules.map((module) => {
                                const draft =
                                  customRoleDevicePermissions.find((permission) => permission.module_key === module.module_key) ??
                                  emptyPermission(module.module_key);
                                return (
                                  <tr key={module.module_key} className="border-t border-card-border">
                                    <td className="px-3 py-2 text-sm font-medium text-foreground">
                                      <label className="inline-flex cursor-pointer items-center gap-2" title={`Toggle all permissions for ${module.label}`}>
                                        <BulkPermissionCheckbox
                                          checked={ACTION_COLUMNS.filter((column) => {
                                            const action = column.key.replace("can_", "") as PermissionAction;
                                            const adminDefaultGrant = user?.role === "admin" && ADMIN_DEFAULT_GRANT_MODULE_KEYS.has(module.module_key) && module.available_actions.includes(action);
                                            return selectedPermissionDeviceId.length > 0 && (adminDefaultGrant || (module.available_actions.includes(action) && Boolean((module as unknown as Record<string, unknown>)[column.key])));
                                          }).every((column) => Boolean(draft[column.key]))}
                                          values={ACTION_COLUMNS.filter((column) => {
                                            const action = column.key.replace("can_", "") as PermissionAction;
                                            const adminDefaultGrant = user?.role === "admin" && ADMIN_DEFAULT_GRANT_MODULE_KEYS.has(module.module_key) && module.available_actions.includes(action);
                                            return selectedPermissionDeviceId.length > 0 && (adminDefaultGrant || (module.available_actions.includes(action) && Boolean((module as unknown as Record<string, unknown>)[column.key])));
                                          }).map((column) => Boolean(draft[column.key]))}
                                          disabled={!selectedPermissionDeviceId || roleDevicePermissionsQuery.isLoading}
                                          label={`Toggle all permissions for ${module.label}`}
                                          onChange={(checked) => setCustomRoleDevicePermissions((current) => applyPermissionChanges(current, ACTION_COLUMNS.flatMap((column) => {
                                            const action = column.key.replace("can_", "") as PermissionAction;
                                            const adminDefaultGrant = user?.role === "admin" && ADMIN_DEFAULT_GRANT_MODULE_KEYS.has(module.module_key) && module.available_actions.includes(action);
                                            const currentDraft = current.find((permission) => permission.module_key === module.module_key) ?? emptyPermission(module.module_key);
                                            const canToggle = canTogglePermissionCheckbox({ moduleKey: module.module_key, targetKind: selectedCustomRole?.is_default ? ((selectedCustomRole.key as "distributor" | "user") ?? "user") : "custom", checked: Boolean(currentDraft[column.key]), baseCanGrant: selectedPermissionDeviceId.length > 0 && (adminDefaultGrant || (module.available_actions.includes(action) && Boolean((module as unknown as Record<string, unknown>)[column.key]))) });
                                            return canToggle ? [{ moduleKey: module.module_key, action: column.key, checked }] : [];
                                          })))}
                                        />
                                        <span>{module.label}</span>
                                      </label>
                                    </td>
                                    {ACTION_COLUMNS.map((column) => {
                                      const action = column.key.replace("can_", "") as PermissionAction;
                                      const adminDefaultGrant =
                                        user?.role === "admin" &&
                                        ADMIN_DEFAULT_GRANT_MODULE_KEYS.has(module.module_key) &&
                                        module.available_actions.includes(action);
                                      const canGrant =
                                        selectedPermissionDeviceId.length > 0 &&
                                        (adminDefaultGrant ||
                                          (module.available_actions.includes(action) &&
                                            Boolean((module as unknown as Record<string, unknown>)[column.key])));
                                      const canToggle = canTogglePermissionCheckbox({
                                        moduleKey: module.module_key,
                                        targetKind: selectedCustomRole?.is_default
                                          ? ((selectedCustomRole.key as "distributor" | "user") ?? "user")
                                          : "custom",
                                        checked: Boolean(draft[column.key]),
                                        baseCanGrant: canGrant,
                                      });
                                      return (
                                        <td key={column.key} className="px-3 py-2 text-center">
                                          <input
                                            type="checkbox"
                                            checked={Boolean(draft[column.key])}
                                            disabled={!canToggle || roleDevicePermissionsQuery.isLoading}
                                            onChange={(event) => {
                                              const checked = event.target.checked;
                                              setCustomRoleDevicePermissions((current) => {
                                                const found = current.some((item) => item.module_key === module.module_key);
                                                if (!found) {
                                                  return [...current, { ...emptyPermission(module.module_key), [column.key]: checked }];
                                                }
                                                return current.map((item) =>
                                                  item.module_key === module.module_key
                                                    ? { ...item, [column.key]: checked }
                                                    : item,
                                                );
                                              });
                                            }}
                                            className="h-4 w-4 disabled:opacity-30"
                                          />
                                        </td>
                                      );
                                    })}
                                  </tr>
                                );
                              })}
                              {grantableDeviceRoleModules.length === 0 ? (
                                <tr>
                                  <td colSpan={ACTION_COLUMNS.length + 1} className="px-3 py-6 text-center text-sm text-muted">
                                    No device modules are grantable from your current role.
                                  </td>
                                </tr>
                              ) : null}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ) : null}
                  </div>
                ) : null}
                <div className="flex justify-end gap-2 border-t border-card-border px-5 py-4">
                  <button
                    type="button"
                    onClick={() => {
                      setRoleDialog(null);
                      setSelectedCustomRoleId(null);
                    }}
                    className="h-10 rounded-lg border border-card-border px-4 text-sm font-semibold text-foreground"
                  >
                    Cancel
                  </button>
                  {roleDialog === "create" ? (
                    <Button
                      type="button"
                      variant="primary"
                      size="md"
                      disabled={!customRoleName.trim() || createCustomRoleMutation.isPending}
                      onClick={() => createCustomRoleMutation.mutate()}
                    >
                      {createCustomRoleMutation.isPending ? "Creating..." : "Create role"}
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      variant="primary"
                      size="md"
                      disabled={(!selectedCustomRole?.is_default && !customRoleEditName.trim()) || saveCustomRoleMutation.isPending}
                      onClick={() => saveCustomRoleMutation.mutate()}
                    >
                      {saveCustomRoleMutation.isPending ? "Saving..." : "Save role"}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ) : null}
          <ListSurface>
            <ListSurfaceHeader
              title="Workspace roles"
              description="Manage Distributor, User, and custom role permissions within this workspace."
              meta={
                <ListCount
                  total={customRoles.length}
                  filtered={filteredCustomRoles.length}
                  noun={filteredCustomRoles.length === 1 ? "role" : "roles"}
                />
              }
              controls={
                <ListToolbar>
                  <ListToolbarGroup grow className="lg:flex-[0_1_40%]">
                    <input
                      value={search}
                      onChange={(event) => setSearch(event.target.value)}
                      placeholder="Search role name, description, or key..."
                      className={tableHeaderSearchInputClass}
                    />
                  </ListToolbarGroup>
                  {search.trim() ? (
                    <ToolbarIconButton icon={FilterX} label="Clear filters" onClick={() => setSearch("")} />
                  ) : null}
                  {canCreateCustomRoles ? (
                    <ToolbarIconButton
                      icon={Plus}
                      label="Create role"
                      variant="primary"
                      onClick={() => {
                        setCustomRoleName("");
                        setCustomRoleDescription("");
                        setFormError(null);
                        setRoleDialog("create");
                      }}
                    />
                  ) : null}
                </ListToolbar>
              }
            />
            <ListSurfaceBody className="overflow-x-auto">
              {customRoleListMessage ? (
                <EmptyState
                  title="Roles unavailable"
                  description={customRoleListMessage}
                  className="rounded-none border-0 border-t"
                />
              ) : customRolesQuery.isLoading || effectivePermissions.isLoading ? (
                <div className="px-4 py-6 text-sm text-muted">Loading roles...</div>
              ) : customRoles.length === 0 ? (
                <EmptyState
                  title="No roles available"
                  description={
                    canCreateCustomRoles
                      ? "Create a custom role for this workspace."
                      : "You can view custom roles, but creating roles has not been granted."
                  }
                  action={
                    canCreateCustomRoles ? (
                      <Button
                        type="button"
                        variant="primary"
                        size="sm"
                        onClick={() => {
                          setCustomRoleName("");
                          setCustomRoleDescription("");
                          setFormError(null);
                          setRoleDialog("create");
                        }}
                      >
                        Create role
                      </Button>
                    ) : null
                  }
                  className="rounded-none border-0 border-t"
                />
              ) : (
                <table className="min-w-full border-collapse">
                <thead className="bg-surface-secondary">
                    <tr>
                      <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.12em] text-foreground">Name</th>
                      <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.12em] text-foreground">Description</th>
                      <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.12em] text-foreground">Permissions</th>
                      <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.12em] text-foreground">Status</th>
                      <th className="px-4 py-3 text-right text-[11px] font-semibold uppercase tracking-[0.12em] text-foreground">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCustomRoles.length ? (
                      filteredCustomRoles.map((role, index) => {
                        const enabledPermissionCount = role.permissions.filter(
                          (permission) =>
                            permission.can_view ||
                            permission.can_create ||
                            permission.can_edit ||
                            permission.can_delete ||
                            permission.can_export ||
                            permission.can_manage_permissions,
                        ).length;
                        return (
                          <tr
                            key={role.id}
                            className="border-t border-card-border"
                            style={{
                              background: index % 2 === 1 ? "rgba(59,130,246,0.05)" : "var(--bg-surface)",
                            }}
                          >
                            <td className="px-4 py-4 align-middle">
                              <div className="font-semibold text-foreground">{role.name}</div>
                              <div className="mt-1 text-xs text-muted">
                                {role.is_default ? "Workspace default role" : role.key}
                              </div>
                            </td>
                            <td className="px-4 py-4 align-middle text-sm text-muted">
                              {role.is_default ? "Workspace default role permissions" : role.description ?? "-"}
                            </td>
                            <td className="px-4 py-4 align-middle text-sm text-muted">
                              {enabledPermissionCount} module{enabledPermissionCount === 1 ? "" : "s"}
                            </td>
                            <td className="px-4 py-4 align-middle text-sm text-foreground">
                              {role.is_active ? "Active" : "Inactive"}
                            </td>
                            <td className="px-4 py-4 align-middle">
                              <div className="flex justify-end gap-2">
                                {canEditCustomRoles ? (
                                  <button
                                    type="button"
                                    className="inline-flex min-h-9 min-w-[36px] items-center justify-center rounded-xl border border-primary/15 bg-primary/10 px-[10px] text-primary transition-colors hover:border-primary/25 hover:bg-primary/15"
                                    onClick={() => {
                                      setSelectedCustomRoleId(role.id);
                                      setRoleDialog("edit");
                                    }}
                                    title={`Edit ${role.name}`}
                                  >
                                    <Pencil size={14} />
                                  </button>
                                ) : null}
                                {canDeleteCustomRoles && !role.is_default ? (
                                  <button
                                    type="button"
                                    className="inline-flex min-h-9 min-w-[36px] items-center justify-center rounded-xl border border-[rgb(255_170_170)] bg-[rgb(255_188_188)] px-[10px] text-[#7f1d1d] transition-colors hover:border-[rgb(207_81_81)] hover:bg-[rgb(255_188_188)]"
                                    onClick={async () => {
                                      const confirmed = await dialogs.confirm({
                                        title: `Delete custom role "${role.name}"?`,
                                        description: "This removes the custom role and its workspace-specific permission settings.",
                                        confirmLabel: "Delete role",
                                        tone: "danger",
                                      });
                                      if (!confirmed) {
                                        return;
                                      }
                                      deleteCustomRoleMutation.mutate(role.id);
                                    }}
                                    title={`Delete ${role.name}`}
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                ) : null}
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={5} className="px-4 py-9 text-center text-sm text-muted">
                          No roles match the current filters.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </ListSurfaceBody>
          </ListSurface>
        </div>
      </EnterprisePageShell>
    );
  }

  return (
    <EnterprisePageShell
      hideHeader
      title="Permissions"
      description="Define page-based access for each role, while only exposing actions that actually make sense for the underlying resource."
    >
      {toast ? <FlashToast message={toast} onDone={() => setToast(null)} /> : null}

      <ListSurface className="grid gap-4 p-5">
        <ListSurfaceHeader
          className="px-0 pt-0"
          title="Permission matrix"
          description="Review module visibility and actions for the selected role before saving."
          meta={
            <ListCount
              total={modules.length}
              filtered={filteredModules.length}
              noun={filteredModules.length === 1 ? "module" : "modules"}
              filteredLabel="Matching this role and search"
            />
          }
          controls={
            <ListToolbar>
              <ListToolbarGroup grow className="lg:flex-[0_1_40%]">
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search by module, key, or description"
                  className={tableHeaderSearchInputClass}
                />
              </ListToolbarGroup>
              <ListToolbarGroup>
                <ThemedSelect
                  value={selectedRole}
                  onChange={(value) => {
                    setSelectedRole(value as PermissionManagedRole);
                    setFormError(null);
                  }}
                  options={roles.map((role) => ({ value: role.key, label: role.label }))}
                  ariaLabel="Select role"
                  className="min-w-[180px]"
                  triggerClassName={tableHeaderSelectTriggerClass}
                />
              </ListToolbarGroup>
              {search.trim() ? (
                <ToolbarIconButton icon={FilterX} label="Clear filters" onClick={() => setSearch("")} />
              ) : null}
              <ToolbarIconButton icon={RotateCcw} label="Reset draft" onClick={resetFromServer} />
              <ToolbarIconButton
                icon={Save}
                label={`Save ${selectedRoleLabel}`}
                variant="primary"
                disabled={saveMutation.isPending || query.isLoading}
                onClick={() => {
                  setFormError(null);
                  saveMutation.mutate();
                }}
              />
            </ListToolbar>
          }
        />
        {formError ? (
          <div className="rounded-xl border border-[#fecaca] bg-[#fef2f2] px-3 py-2 text-sm text-[#b91c1c]">
            {formError}
          </div>
        ) : null}

        {query.isLoading ? (
          <div className="rounded-[18px] border border-dashed border-[var(--table-border)] bg-muted-bg/30 px-4 py-8 text-sm text-muted">
            Loading permission matrix...
          </div>
        ) : query.isError ? (
          <div className="rounded-[18px] border border-[#fecaca] bg-[#fff5f5] px-4 py-8 text-sm text-[#b91c1c]">
            {errorDetail(query.error)}
          </div>
        ) : (
          <div className="grid gap-5">
            {Object.entries(groupedModules).map(([groupKey, groupModules]) => (
              <ListSurface key={groupKey} className="shadow-[0_1px_4px_rgba(0,0,0,0.04)]">
                <ListSurfaceHeader>
                  <div>
                    <div className="flex items-center gap-2">
                      <BulkPermissionCheckbox
                        checked={groupModules.flatMap((module) => ACTION_COLUMNS.filter((column) => module.available_actions.includes(column.key.replace("can_", "") as PermissionAction)).map((column) => Boolean((permissionMap[module.key] ?? emptyPermission(module.key))[column.key]))).every(Boolean)}
                        values={groupModules.flatMap((module) => ACTION_COLUMNS.filter((column) => module.available_actions.includes(column.key.replace("can_", "") as PermissionAction)).map((column) => Boolean((permissionMap[module.key] ?? emptyPermission(module.key))[column.key])))}
                        disabled={groupModules.length === 0}
                        label={`Toggle all ${GROUP_LABELS[groupKey] ?? groupKey} permissions`}
                        onChange={(checked) => setDrafts((prev) => ({
                          ...prev,
                          [selectedRole]: applyPermissionChanges(prev[selectedRole] ?? [], groupModules.flatMap((module) => ACTION_COLUMNS.flatMap((column) => {
                            const action = column.key.replace("can_", "") as PermissionAction;
                            const draft = (prev[selectedRole] ?? []).find((permission) => permission.module_key === module.key) ?? emptyPermission(module.key);
                            const canToggle = canTogglePermissionCheckbox({ moduleKey: module.key, targetKind: selectedRole, checked: Boolean(draft[column.key]), baseCanGrant: module.available_actions.includes(action) });
                            return canToggle ? [{ moduleKey: module.key, action: column.key, checked }] : [];
                          }))),
                        }))}
                      />
                      <h2 className="text-sm font-semibold text-foreground">{GROUP_LABELS[groupKey] ?? groupKey}</h2>
                    </div>
                    <p className="text-xs text-muted">Role-based access for {GROUP_LABELS[groupKey]?.toLowerCase() ?? groupKey} navigation and route areas.</p>
                  </div>
                </ListSurfaceHeader>
                <ListSurfaceBody className="overflow-x-auto">
                  <table className="min-w-full border-collapse">
                <thead className="bg-surface-secondary">
                      <tr>
                        <th className="px-4 py-3 text-[12px] font-semibold uppercase tracking-[0.08em] text-muted">Module</th>
                        {ACTION_COLUMNS.map((column) => (
                          <th
                            key={column.key}
                            className="px-3 py-3 text-center text-[12px] font-semibold uppercase tracking-[0.08em] text-muted"
                          >
                            <label className="inline-flex cursor-pointer items-center justify-center gap-1.5" title={`Toggle ${column.label} for ${GROUP_LABELS[groupKey] ?? groupKey}`}>
                              <BulkPermissionCheckbox
                                checked={groupModules.filter((module) => module.available_actions.includes(column.key.replace("can_", "") as PermissionAction)).every((module) => Boolean((permissionMap[module.key] ?? emptyPermission(module.key))[column.key]))}
                                values={groupModules.filter((module) => module.available_actions.includes(column.key.replace("can_", "") as PermissionAction)).map((module) => Boolean((permissionMap[module.key] ?? emptyPermission(module.key))[column.key]))}
                                disabled={!groupModules.some((module) => module.available_actions.includes(column.key.replace("can_", "") as PermissionAction))}
                                label={`Toggle ${column.label} for ${GROUP_LABELS[groupKey] ?? groupKey}`}
                                onChange={(checked) => setDrafts((prev) => ({
                                  ...prev,
                                  [selectedRole]: applyPermissionChanges(prev[selectedRole] ?? [], groupModules.flatMap((module) => {
                                    const action = column.key.replace("can_", "") as PermissionAction;
                                    const draft = (prev[selectedRole] ?? []).find((permission) => permission.module_key === module.key) ?? emptyPermission(module.key);
                                    const canToggle = canTogglePermissionCheckbox({ moduleKey: module.key, targetKind: selectedRole, checked: Boolean(draft[column.key]), baseCanGrant: module.available_actions.includes(action) });
                                    return canToggle ? [{ moduleKey: module.key, action: column.key, checked }] : [];
                                  })),
                                }))}
                              />
                              <span>{column.label}</span>
                            </label>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {groupModules.map((module, index) => {
                        const draft = permissionMap[module.key] ?? {
                          ...emptyPermission(module.key),
                        };
                        return (
                          <tr key={module.key} className={index % 2 === 1 ? "border-t border-[var(--card-border)] bg-primary/5" : "border-t border-[var(--card-border)] bg-[var(--bg-surface)]"}>
                            <td className="px-4 py-3 align-top">
                              <div className="flex items-center gap-2 font-medium text-foreground">
                                <BulkPermissionCheckbox
                                  checked={ACTION_COLUMNS.filter((column) => module.available_actions.includes(column.key.replace("can_", "") as PermissionAction)).every((column) => Boolean(draft[column.key]))}
                                  values={ACTION_COLUMNS.filter((column) => module.available_actions.includes(column.key.replace("can_", "") as PermissionAction)).map((column) => Boolean(draft[column.key]))}
                                  label={`Toggle all permissions for ${module.label}`}
                                  onChange={(checked) => setDrafts((prev) => ({
                                    ...prev,
                                    [selectedRole]: applyPermissionChanges(prev[selectedRole] ?? [], ACTION_COLUMNS.flatMap((column) => {
                                      const action = column.key.replace("can_", "") as PermissionAction;
                                      const currentDraft = (prev[selectedRole] ?? []).find((permission) => permission.module_key === module.key) ?? emptyPermission(module.key);
                                      const canToggle = canTogglePermissionCheckbox({ moduleKey: module.key, targetKind: selectedRole, checked: Boolean(currentDraft[column.key]), baseCanGrant: module.available_actions.includes(action) });
                                      return canToggle ? [{ moduleKey: module.key, action: column.key, checked }] : [];
                                    })),
                                  }))}
                                />
                                <span>{module.label}</span>
                              </div>
                              <div className="mt-1 text-xs text-muted">
                                {module.description || modulesByKey[module.key]?.key}
                              </div>
                            </td>
                            {ACTION_COLUMNS.map((column) => {
                              const action = column.key.replace("can_", "") as PermissionAction;
                              const canToggle = canTogglePermissionCheckbox({
                                moduleKey: module.key,
                                targetKind: selectedRole,
                                checked: Boolean(draft[column.key]),
                                baseCanGrant: module.available_actions.includes(action),
                              });
                              return (
                                <td key={column.key} className="px-3 py-3 text-center">
                                  {module.available_actions.includes(action) ? (
                                    <input
                                      type="checkbox"
                                      checked={Boolean(draft[column.key])}
                                      disabled={!canToggle}
                                      onChange={(event) => {
                                        const checked = event.target.checked;
                                        setDrafts((prev) => ({
                                          ...prev,
                                          [selectedRole]: (() => {
                                            const current = prev[selectedRole] ?? [];
                                            const found = current.some((permission) => permission.module_key === module.key);
                                            if (!found) {
                                              return [
                                                ...current,
                                                { ...emptyPermission(module.key), [column.key]: checked },
                                              ];
                                            }
                                            return current.map((permission) =>
                                              permission.module_key === module.key
                                                ? { ...permission, [column.key]: checked }
                                                : permission,
                                            );
                                          })(),
                                        }));
                                      }}
                                      className="h-4 w-4 cursor-pointer rounded border-[#d1d5db] text-[var(--btn-primary-to)] focus:ring-[var(--btn-primary-to)]/20"
                                    />
                                  ) : (
                                    <span className="text-xs font-medium text-slate-300">-</span>
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </ListSurfaceBody>
              </ListSurface>
            ))}
            {filteredModules.length === 0 ? (
              <EmptyState
                title="No modules match this filter"
                description="Try a broader search term to review more permissions."
              />
            ) : null}
          </div>
        )}
      </ListSurface>
    </EnterprisePageShell>
  );
}
